import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Palette, Upload, Eye, Globe } from "lucide-react";

export default function BrandSettingsPage() {
  const [brand, setBrand] = useState({ company_name: "", tagline: "", logo_url: "", primary_color: "#0047FF", contact_email: "", contact_url: "", custom_share_domain: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/brand").then((r) => setBrand({ ...brand, ...r.data })); // eslint-disable-line
  }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/brand", brand);
      toast.success("Brand saved. Applied to reports and share links.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">White-label</div>
      <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Agency branding.</h1>
      <p className="mt-2 text-sm text-[#525B6A] max-w-xl">Apply your agency&apos;s logo, name, and accent color to scheduled reports and shared report views. Clients see your brand, not SerpNex.</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <form onSubmit={save} className="lg:col-span-3 card-crisp p-6 space-y-4" data-testid="brand-form">
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Company name</label>
            <input data-testid="brand-name" value={brand.company_name || ""} onChange={(e) => setBrand({ ...brand, company_name: e.target.value })} placeholder="Northlane Agency" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Tagline (footer text)</label>
            <input data-testid="brand-tagline" value={brand.tagline || ""} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} placeholder="SEO reporting powered by Northlane" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Logo URL <span className="text-[#8A94A6]">(hosted PNG or JPG, ~200 × 60)</span></label>
            <input data-testid="brand-logo" value={brand.logo_url || ""} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} placeholder="https://your-agency.com/logo.png" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Accent color</label>
              <div className="flex gap-2">
                <input type="color" data-testid="brand-color" value={brand.primary_color || "#0047FF"} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} className="h-10 w-14 rounded-md border border-[#E2E8F0] p-1 bg-white" />
                <input value={brand.primary_color || ""} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm tabular-nums" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Contact email (optional)</label>
              <input data-testid="brand-email" value={brand.contact_email || ""} onChange={(e) => setBrand({ ...brand, contact_email: e.target.value })} placeholder="hello@your-agency.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Contact URL (optional)</label>
            <input data-testid="brand-url" value={brand.contact_url || ""} onChange={(e) => setBrand({ ...brand, contact_url: e.target.value })} placeholder="https://your-agency.com/contact" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div className="pt-3 border-t border-[#E2E8F0]">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> Custom share domain (optional)</label>
            <input data-testid="brand-custom-domain" value={brand.custom_share_domain || ""} onChange={(e) => setBrand({ ...brand, custom_share_domain: e.target.value.trim() })} placeholder="reports.your-agency.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm tabular-nums" />
            <p className="mt-1.5 text-[11px] text-[#8A94A6] leading-relaxed">
              Enter the hostname you&apos;d like clients to see in the share URL (e.g. <code className="text-[#0A0D14]">reports.your-agency.com</code>). Then create a <code className="text-[#0A0D14]">CNAME</code> record pointing that hostname to <code className="text-[#0A0D14]">serpnex.com</code>. Share links will use your domain instead of ours.
            </p>
          </div>
          <button data-testid="brand-save" disabled={saving} className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Upload className="h-4 w-4" /> {saving ? "Saving…" : "Save branding"}</button>
        </form>

        <aside className="lg:col-span-2">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-3 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Report preview</div>
          <div className="card-crisp p-5" data-testid="brand-preview" style={{ borderTop: `4px solid ${brand.primary_color || '#0047FF'}` }}>
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="h-8 mb-3 object-contain max-w-[180px]" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: brand.primary_color || '#0047FF' }}>
              {brand.company_name || "SerpNex"} · Weekly report
            </div>
            <div className="mt-2 text-xl font-display font-semibold text-[#0A0D14]">Client project name</div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Tracked</div><div className="stat-value text-xl font-bold text-[#0A0D14]">128</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Top 10</div><div className="stat-value text-xl font-bold" style={{ color: brand.primary_color || '#0047FF' }}>34</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Improved</div><div className="stat-value text-xl font-bold text-emerald-600">12</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Dropped</div><div className="stat-value text-xl font-bold text-red-600">3</div></div>
            </div>
            <div className="mt-4 border-t border-[#E2E8F0] pt-3 text-xs text-[#8A94A6]">{brand.tagline || "Daily rank tracking · transparency-first"}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
